import type { SajuAnalysis } from '../types/input';
import type { SpousePalace } from '../types/compatibility';
import type { TenGod } from '../types/ten-gods';
import { getMainHiddenStem } from '../core/hidden-stems';
import { getTenGod } from '../analysis/ten-gods';

const SPOUSE_STARS_MALE: TenGod[] = ['정재', '편재'];
const SPOUSE_STARS_FEMALE: TenGod[] = ['정관', '편관'];
// 동성 커플: 전통적인 성별 기반 배우자성 이분법이 무너지므로
// 재성(정재/편재)과 관성(정관/편관)을 모두 "이상적 배우자성"으로 포괄 적용한다.
const UNIVERSAL_SPOUSE_STARS: TenGod[] = [...SPOUSE_STARS_MALE, ...SPOUSE_STARS_FEMALE];
const GOOD_STARS: TenGod[] = ['정재', '편재', '정관', '편관', '정인', '편인'];
const CAUTION_STARS: TenGod[] = ['비견', '겁재'];

/**
 * 배우자궁(일지) 분석.
 *
 * 각 사람의 일지 본기가 상대 일간 기준으로 어떤 십신인지 판정하여
 * 배우자궁 조화도를 분석한다.
 */
export function analyzeSpousePalace(
  analysis1: SajuAnalysis,
  analysis2: SajuAnalysis,
): SpousePalace {
  const dayBranch1 = analysis1.fourPillars.day.branch;
  const dayBranch2 = analysis2.fourPillars.day.branch;

  // 각 사람의 일지 본기(정기)
  const mainStem1 = getMainHiddenStem(dayBranch1);
  const mainStem2 = getMainHiddenStem(dayBranch2);

  // 상대방 일간 기준으로 십신 판정
  // person1의 일지 본기가 person2에게 어떤 의미인지
  const star1ForP2 = getTenGod(analysis2.dayMaster, mainStem1);
  // person2의 일지 본기가 person1에게 어떤 의미인지
  const star2ForP1 = getTenGod(analysis1.dayMaster, mainStem2);

  const gender1 = analysis1.input.gender;
  const gender2 = analysis2.input.gender;

  const harmony = evaluateHarmony(star2ForP1, star1ForP2, gender1, gender2);

  const description = `배우자궁: P1 일지 본기→P2(${star1ForP2}), P2 일지 본기→P1(${star2ForP1}) — ${harmony}`;

  return {
    person1DayBranch: dayBranch1,
    person2DayBranch: dayBranch2,
    person1SpouseStar: star2ForP1,
    person2SpouseStar: star1ForP2,
    harmony,
    description,
  };
}

function evaluateHarmony(
  starForP1: TenGod,
  starForP2: TenGod,
  gender1: 'male' | 'female',
  gender2: 'male' | 'female',
): SpousePalace['harmony'] {
  // 동성 커플: 전통적 이분법(남성→정재/편재, 여성→정관/편관)이 성립하지 않으므로
  // 재성·관성 모두를 포괄하는 UNIVERSAL_SPOUSE_STARS 기준으로 판정한다.
  const sameSex = gender1 === gender2;
  const spouseStars1 = sameSex
    ? UNIVERSAL_SPOUSE_STARS
    : gender1 === 'male' ? SPOUSE_STARS_MALE : SPOUSE_STARS_FEMALE;
  const spouseStars2 = sameSex
    ? UNIVERSAL_SPOUSE_STARS
    : gender2 === 'male' ? SPOUSE_STARS_MALE : SPOUSE_STARS_FEMALE;

  const isSpouse1 = spouseStars1.includes(starForP1);
  const isSpouse2 = spouseStars2.includes(starForP2);

  if (isSpouse1 && isSpouse2) return '매우좋음';
  if (isSpouse1 || isSpouse2) return '좋음';

  const isCaution1 = CAUTION_STARS.includes(starForP1);
  const isCaution2 = CAUTION_STARS.includes(starForP2);

  if (isCaution1 && isCaution2) return '주의';
  if (GOOD_STARS.includes(starForP1) || GOOD_STARS.includes(starForP2)) return '보통';

  return '보통';
}
